import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Check, Loader2, Mail, RefreshCw, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { EmailEditTarget, VendorAdminRow, VendorTeamMemberAdminRow } from "./types";

export function VendorEmailResetPanel() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [vendorSearch, setVendorSearch] = useState("");
  const [memberSearch, setMemberSearch] = useState("");
  const [editTarget, setEditTarget] = useState<EmailEditTarget | null>(null);
  const [newEmail, setNewEmail] = useState("");

  const vendorListQuery = useQuery<VendorAdminRow[]>({ queryKey: ["/api/admin/vendors"] });
  const memberListQuery = useQuery<VendorTeamMemberAdminRow[]>({ queryKey: ["/api/admin/vendor-team-members"] });

  const vendorUpdateMutation = useMutation({
    mutationFn: async ({ id, email }: { id: number; email: string }) =>
      apiRequest("PATCH", `/api/admin/vendors/${id}/email`, { email }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/vendors"] });
      toast({ title: "Email updated", description: "The vendor's email address has been saved. Every change is recorded in the admin audit log." });
      setEditTarget(null);
      setNewEmail("");
    },
    onError: (err: any) =>
      toast({ title: "Failed to update email", description: parseErrorMessage(err, "Couldn't update email. Please try again."), variant: "destructive" }),
  });

  const memberUpdateMutation = useMutation({
    mutationFn: async ({ id, email }: { id: number; email: string }) =>
      apiRequest("PATCH", `/api/admin/vendor-team-members/${id}/email`, { email }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/vendor-team-members"] });
      toast({ title: "Email updated", description: "Email changed and notification sent to new address. Every change is recorded in the admin audit log." });
      setEditTarget(null);
      setNewEmail("");
    },
    onError: (err: any) =>
      toast({ title: "Failed to update email", description: parseErrorMessage(err, "Couldn't update email. Please try again."), variant: "destructive" }),
  });

  const isPending = vendorUpdateMutation.isPending || memberUpdateMutation.isPending;

  const vendors = vendorListQuery.data ?? [];
  const filteredVendors = vendors.filter((v) => {
    if (!vendorSearch.trim()) return true;
    const q = vendorSearch.toLowerCase();
    return (
      String(v.name ?? "").toLowerCase().includes(q) ||
      String(v.businessName ?? "").toLowerCase().includes(q) ||
      String(v.email ?? "").toLowerCase().includes(q)
    );
  });

  const members = memberListQuery.data ?? [];
  const filteredMembers = members.filter((m) => {
    if (!memberSearch.trim()) return true;
    const q = memberSearch.toLowerCase();
    return (
      String(m.vendorName ?? "").toLowerCase().includes(q) ||
      `${m.firstName} ${m.lastName}`.toLowerCase().includes(q) ||
      String(m.email ?? "").toLowerCase().includes(q)
    );
  });

  function openEditVendor(v: VendorAdminRow) {
    setEditTarget({ kind: "vendor", row: v });
    setNewEmail(v.email ?? "");
  }

  function openEditMember(m: VendorTeamMemberAdminRow) {
    setEditTarget({ kind: "member", row: m });
    setNewEmail(m.email ?? "");
  }

  function handleSubmit() {
    if (!editTarget) return;
    const trimmed = newEmail.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast({ title: "Invalid email", description: "Please enter a valid email address.", variant: "destructive" });
      return;
    }
    if (editTarget.kind === "vendor") {
      vendorUpdateMutation.mutate({ id: editTarget.row.id, email: trimmed });
    } else {
      memberUpdateMutation.mutate({ id: editTarget.row.id, email: trimmed });
    }
  }

  const dialogDisplayName = editTarget
    ? editTarget.kind === "vendor"
      ? editTarget.row.name
      : `${editTarget.row.firstName} ${editTarget.row.lastName}`
    : "";

  return (
    <>
      <Card className="p-4">
        <div className="mb-3">
          <h2 className="font-semibold text-base">Vendor Email Resets</h2>
          <p className="text-xs text-muted-foreground">
            Override a vendor or team member's email address without requiring an OTP code — for accounts locked out of their current email.
            Every change is recorded in the admin audit log.
          </p>
        </div>

        <Tabs defaultValue="vendors">
          <TabsList className="mb-3">
            <TabsTrigger value="vendors" data-testid="tab-vendor-email-vendors">Vendor Owners</TabsTrigger>
            <TabsTrigger value="members" data-testid="tab-vendor-email-members">Team Members</TabsTrigger>
          </TabsList>

          <TabsContent value="vendors">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex gap-2">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                  <Input
                    className="pl-7 h-8 text-sm w-52"
                    placeholder="Search vendors…"
                    value={vendorSearch}
                    onChange={(e) => setVendorSearch(e.target.value)}
                    data-testid="input-vendor-email-search"
                  />
                </div>
                <Button size="sm" variant="outline" onClick={() => vendorListQuery.refetch()} disabled={vendorListQuery.isFetching} data-testid="button-refresh-vendor-emails">
                  {vendorListQuery.isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            {vendorListQuery.isLoading ? (
              <div className="text-sm text-muted-foreground py-6 text-center">Loading…</div>
            ) : filteredVendors.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center" data-testid="text-no-vendor-email-results">
                {vendorSearch.trim() ? "No vendors match your search." : "No vendors found."}
              </div>
            ) : (
              <div className="border rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-xs uppercase">
                    <tr>
                      <th className="text-left px-3 py-2">Vendor</th>
                      <th className="text-left px-3 py-2">Current Email</th>
                      <th className="text-right px-3 py-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVendors.map((v) => (
                      <tr key={v.id} className="border-t" data-testid={`row-vendor-email-${v.id}`}>
                        <td className="px-3 py-2">
                          <div className="font-medium">{v.name}</div>
                          {v.businessName && <div className="text-xs text-muted-foreground">{v.businessName}</div>}
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground" data-testid={`text-vendor-email-${v.id}`}>
                          {v.email ?? <span className="italic">not set</span>}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={() => openEditVendor(v)}
                            data-testid={`button-edit-vendor-email-${v.id}`}
                          >
                            <Mail className="h-3.5 w-3.5 mr-1" />
                            Edit email
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="members">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex gap-2">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                  <Input
                    className="pl-7 h-8 text-sm w-64"
                    placeholder="Search by vendor or member name…"
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                    data-testid="input-member-email-search"
                  />
                </div>
                <Button size="sm" variant="outline" onClick={() => memberListQuery.refetch()} disabled={memberListQuery.isFetching} data-testid="button-refresh-member-emails">
                  {memberListQuery.isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            {memberListQuery.isLoading ? (
              <div className="text-sm text-muted-foreground py-6 text-center">Loading…</div>
            ) : filteredMembers.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center" data-testid="text-no-member-email-results">
                {memberSearch.trim() ? "No team members match your search." : "No team members found."}
              </div>
            ) : (
              <div className="border rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-xs uppercase">
                    <tr>
                      <th className="text-left px-3 py-2">Member</th>
                      <th className="text-left px-3 py-2">Role</th>
                      <th className="text-left px-3 py-2">Current Email</th>
                      <th className="text-right px-3 py-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMembers.map((m) => (
                      <tr key={m.id} className="border-t" data-testid={`row-member-email-${m.id}`}>
                        <td className="px-3 py-2">
                          <div className="font-medium">{m.firstName} {m.lastName}</div>
                          <div className="text-xs text-muted-foreground">{m.vendorName}</div>
                        </td>
                        <td className="px-3 py-2">
                          <span className="capitalize text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{m.role}</span>
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground" data-testid={`text-member-email-${m.id}`}>
                          {m.email ?? <span className="italic">not set</span>}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={() => openEditMember(m)}
                            data-testid={`button-edit-member-email-${m.id}`}
                          >
                            <Mail className="h-3.5 w-3.5 mr-1" />
                            Edit email
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </Card>

      <Dialog
        open={!!editTarget}
        onOpenChange={(open) => {
          if (!open && !isPending) {
            setEditTarget(null);
            setNewEmail("");
          }
        }}
      >
        <DialogContent
          onInteractOutside={(e) => { if (isPending) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (isPending) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle>
              {editTarget?.kind === "member" ? "Edit team member email" : "Edit vendor email"}
            </DialogTitle>
            <DialogDescription>
              Set a new email address for <strong>{dialogDisplayName}</strong>.
              {editTarget?.kind === "member" && (
                <> This updates the individual team member's login email.</>
              )}
              {" "}This bypasses the normal OTP verification and is logged in the audit trail. Use only when the account is locked out of its current email.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="admin-vendor-new-email">New email address</Label>
            <Input
              id="admin-vendor-new-email"
              type="email"
              placeholder="member@example.com"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
              disabled={isPending}
              data-testid="input-admin-vendor-new-email"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setEditTarget(null); setNewEmail(""); }}
              disabled={isPending}
              data-testid="button-cancel-vendor-email"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isPending}
              data-testid="button-save-vendor-email"
            >
              {isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
              Save email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
